/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  fetchGoals,
  updateGoal,
  deleteGoal as _deleteGoal,
} from "../../../api";
import { RootState } from "../../store";
import { Edits, Goal, LoadStatus } from "../../../types";

export interface GoalsState {
  goals: Record<string, Goal>;
  goalEdits: Record<string, Edits<Goal>>;
  goalLoadStatus: LoadStatus;
}

const initialState: GoalsState = {
  goals: {},
  goalEdits: {},
  goalLoadStatus: LoadStatus.NONE,
};

/** Actions */

export const loadGoals = createAsyncThunk(
  "goals/loadGoals",
  async (_, thunkAPI): Promise<Goal[]> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    return await fetchGoals(state.login.accessToken);
  }
);

export const addOrUpdateGoal = createAsyncThunk(
  "goals/addOrUpdateGoal",
  async (data: Partial<Goal>, thunkAPI): Promise<Goal> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    return await updateGoal(state.login.accessToken, data);
  }
);

export const deleteGoal = createAsyncThunk(
  "goals/deleteGoal",
  async (id: string, thunkAPI): Promise<void> => {
    if (!id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    await _deleteGoal(state.login.accessToken, id);
  }
);

export const revertGoal = createAsyncThunk(
  "goals/revertGoal",
  async (id: string, thunkAPI): Promise<Goal> => {
    if (!id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken)
      throw new Error("login required");
    const edits = state.goals.goalEdits[id];
    if (
      !edits ||
      !edits.oldData ||
      `${JSON.stringify(edits.oldData)}` === `${JSON.stringify(edits.newData)}`
    ) {
      throw new Error("no changes to revert");
    }
    return await updateGoal(state.login.accessToken, edits.oldData);
  }
);

export const goalsSlice = createSlice({
  name: "goals",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // load goals
      .addCase(loadGoals.pending, (state) => {
        state.goalLoadStatus = LoadStatus.IN_PROGRESS;
      })
      .addCase(loadGoals.fulfilled, (state, action) => {
        for (const goal of action.payload) {
          state.goals[goal.id] = goal;
          state.goalEdits[goal.id] = {
            oldData: goal,
          };
        }
        state.goalLoadStatus = LoadStatus.DONE;
      })
      .addCase(loadGoals.rejected, (state) => {
        state.goalLoadStatus = LoadStatus.FAILED;
      })
      // add or update goal
      .addCase(addOrUpdateGoal.fulfilled, (state, action) => {
        const goal = action.payload;
        if (state.goals[goal.id]) {
          state.goalEdits[goal.id].newData = goal;
        } else {
          state.goalEdits[goal.id] = {
            oldData: goal,
            created: true,
          };
        }
        state.goals[goal.id] = goal;
      })
      // delete goal
      .addCase(deleteGoal.fulfilled, (state, action) => {
        const id = action.meta.arg;
        delete state.goals[id];
      })
      // revert goal
      .addCase(revertGoal.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.goals[id] = action.payload;
        state.goalEdits[id].oldData = action.payload;
        state.goalEdits[id].newData = undefined;
      });
  },
});

export default goalsSlice.reducer;
